import * as R from "ramda";
import React from "react";
import Autosuggest from "react-autosuggest";
import { FaPlus } from "react-icons/fa";
import { IDisplayInfoResponse } from "ts/api/ResolveApi/resolve-api.types";
import AutoComplete from "../../../../components/pages/CourseAuthor/Autocomplete";
import { FormContainer } from "../../../../components/pages/CourseAuthor/FormElements";
import { api } from "../../../../dependencies";
import { toTuple } from "../../../../parsers/resolve";
import { IUser } from "../../../Analytics/Common";
import { AddButton, ButtonRow, CustomSearchInput, SearchField } from "../../../Common/Components/FormComponents";
import { SectionTitle } from "../../../Common/Styles/containers";
import UserRow from "../../../Common/Styles/UserLink";

interface IAdminsProps {
  admins: IUser[];
  accountId: string;
  organizationId: string;
  addNewAdmin: (adminId: string) => void;
  removeAdmin: (adminId: string) => void;
}

const sortByName = R.sortBy(R.prop("displayName"));

type EditorIdWithDisplayInfo = [string, IDisplayInfoResponse];

const Admins: React.FC<IAdminsProps> = props => {
  const autosuggestRef = React.useRef<AutoComplete<EditorIdWithDisplayInfo>>(null);
  const [eligibleAdmins, setEligibleAdmins] = React.useState<EditorIdWithDisplayInfo[]>([]);
  const [selectedAdmin, setSelectedAdmin] = React.useState<EditorIdWithDisplayInfo | null>(null);
  const { admins } = props;
  const onSuggestionsFetchRequested = async ({ value }: Autosuggest.SuggestionsFetchRequestedParams) => {
    const [error, response] = await api.autocomplete.getEligibleEditors(props.accountId, props.organizationId)(value);
    if (error || !response) {
      return;
    }
    const data = R.map(toTuple, R.slice<string>(0, 50, response.data));
    const [resolveError, resolveResp] = await api.resolve.displayInfo.post(props.accountId, data);
    if (resolveError || !resolveResp) {
      return;
    }
    const allResolvedDataWithIds = R.zip(response.data, resolveResp.data)
      .filter(([_, resolvedData]) => !!resolvedData)
      .map(([id, resolvedData]) => [id, resolvedData!.value]) as Array<[string, IDisplayInfoResponse]>;
    setEligibleAdmins(allResolvedDataWithIds);
  };
  const selectNewAdmin = (selected: [string, IDisplayInfoResponse]) => {
    admins.every(admin => admin.accountId !== selected[0]) && setSelectedAdmin(selected);
  };
  const addSelectedAdmin = () => {
    if (selectedAdmin) {
      props.addNewAdmin(selectedAdmin[0]);
      autosuggestRef.current && autosuggestRef.current.clearInput();
      setSelectedAdmin(null);
    }
  };
  const removeAdmin = (adminId: string) => () => {
    if (!adminId.includes(props.accountId)) {
      props.removeAdmin(adminId);
    }
  };
  const renderUserRow = (user: IUser) => <UserRow key={user.accountId} user={user} removeUser={removeAdmin} />;

  return (
    <div>
      <SectionTitle>Administrators</SectionTitle>
      <FormContainer>
        <SearchField legend={"Administrator name"}>
          <AutoComplete
            ref={autosuggestRef}
            fetchSuggestions={onSuggestionsFetchRequested}
            htmlName={"choose-admin"}
            items={eligibleAdmins}
            itemValue={R.pathOr("", [1, "displayName"])}
            placeholderText="Type here to look for a User or Group"
            renderInputComponent={CustomSearchInput}
            setSelected={selectNewAdmin}
          />
        </SearchField>
        <ButtonRow width={15} margin={1}>
          <AddButton onClick={addSelectedAdmin}>
            <span>
              <FaPlus /> Add
            </span>
          </AddButton>
        </ButtonRow>
      </FormContainer>
      {sortByName(admins).map(renderUserRow)}
    </div>
  );
};

export default Admins;
